import { Utensils, Flame, type LucideIcon } from "lucide-react";
import { useCountUp } from "../lib/useCountUp";
import { useT } from "../lib/i18n";
import { useChartColor } from "../lib/chartColors";
import { useUnits } from "../lib/useUnits";
import { MacroBar } from "./MacroBar";
import { MicrosList } from "./MicrosList";
import type { MacroTotals } from "@shared/types";

interface Props {
  eaten: number;       // consumed.calories
  burned: number;      // burned_kcal
  remaining: number;   // budget - eaten (may be negative)
  budget: number;      // daily_calories + burned
  consumed: MacroTotals;
  targets: { carbs_g: number; protein_g: number; fat_g: number; sugar_g: number; fiber_g: number };
  onDetails: () => void;
}

export function SummaryCard({ eaten, burned, remaining, budget, consumed, targets, onDetails }: Props) {
  const t = useT();
  const units = useUnits();
  const e = (kcal: number) => units.energy(kcal).value;
  const over = remaining < 0;
  const animated = useCountUp(e(Math.abs(remaining)));

  const size = 150;
  const r = 64;
  const stroke = 12;
  const circ = 2 * Math.PI * r;
  const pct = budget > 0 ? Math.min(1, eaten / budget) : 0;
  const offset = circ * (1 - pct);
  // Over target is stated neutrally — never red, never an alert (UI plan §9).
  const overRing = useChartColor("ring-over");
  const ringColor = over ? overRing : "rgb(var(--brand))";

  return (
    <section className="glass rounded-3xl p-5">
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm font-semibold text-fg-secondary">{t("summary.title")}</span>
        <button onClick={onDetails} className="text-sm font-semibold text-brand press">
          {t("summary.details")} ›
        </button>
      </div>

      <div className="flex items-center justify-between">
        <Flank value={e(eaten)} icon={Utensils} label={t("summary.eaten")} />
        <div className="relative" style={{ width: size, height: size }}>
          <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90">
            <circle cx={size / 2} cy={size / 2} r={r} fill="none" strokeWidth={stroke}
              style={{ stroke: "var(--surface-strong)" }} />
            <circle cx={size / 2} cy={size / 2} r={r} fill="none" strokeWidth={stroke} strokeLinecap="round"
              strokeDasharray={circ} strokeDashoffset={offset}
              style={{ stroke: ringColor, transition: "stroke-dashoffset 600ms cubic-bezier(0.4,0,0.2,1)" }} />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-3xl font-semibold tabular-nums leading-none">{animated}</span>
            <span className="text-sm text-fg-muted mt-1">{over ? t("summary.over") : t("summary.remaining")}</span>
          </div>
        </div>
        <Flank value={e(burned)} icon={Flame} label={t("summary.burned")} />
      </div>

      <div className="mt-4 space-y-3">
        <MacroBar label={t("macro.carbs")}   value={consumed.carbs_g}   target={targets.carbs_g}   color="rgb(var(--accent-move))" />
        <MacroBar label={t("macro.protein")} value={consumed.protein_g} target={targets.protein_g} color="rgb(var(--accent-protein))" />
        <MacroBar label={t("macro.fat")}     value={consumed.fat_g}     target={targets.fat_g}     color="rgb(var(--warn))" />
        <MacroBar label={t("macro.sugar")}   value={consumed.sugar_g}   target={targets.sugar_g}   color="rgb(var(--accent-protein))" />
        <MacroBar label={t("macro.fiber")}   value={consumed.fiber_g}   target={targets.fiber_g}   color="rgb(var(--success))" />
      </div>

      <MicrosList micros={consumed.micros} />
    </section>
  );
}

function Flank({ value, label, icon: FlankIcon }: { value: number; label: string ; icon?: LucideIcon }) {
  return (
    <div className="text-center w-16">
      <div className="text-lg font-bold tabular-nums">{Math.round(value)}</div>
      <div className="text-sm text-fg-muted mt-0.5">{FlankIcon && <FlankIcon aria-hidden="true" size={14} className="inline -mt-0.5 mr-1 text-fg-muted" />}{label}</div>
    </div>
  );
}
